import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  getPendingMutations,
  removeMutation,
  incrementRetry,
  getPendingCount,
} from "@/lib/offlineQueue";
import { toast } from "sonner";

const MAX_RETRIES = 5;

export function useOfflineSync() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const syncingRef = useRef(false);

  // Track online/offline status
  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online
      syncPendingMutations();
    };
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    // Check pending count on mount
    getPendingCount().then(setPendingCount);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const syncPendingMutations = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return;
    syncingRef.current = true;
    setIsSyncing(true);

    try {
      const mutations = await getPendingMutations();
      if (mutations.length === 0) {
        setIsSyncing(false);
        syncingRef.current = false;
        return;
      }

      let synced = 0;
      let failed = 0;

      for (const mutation of mutations) {
        if (mutation.retries >= MAX_RETRIES) {
          // Too many retries — remove and count as failed
          await removeMutation(mutation.id);
          failed++;
          continue;
        }

        try {
          let result;
          const table = supabase.from(mutation.table as any);

          switch (mutation.operation) {
            case "insert":
              result = await table.insert(mutation.payload as any);
              break;
            case "update": {
              const { id: rowId, ...rest } = mutation.payload;
              result = await table.update(rest as any).eq("id", rowId as string);
              break;
            }
            case "upsert":
              result = await table.upsert(mutation.payload as any);
              break;
            case "delete":
              result = await table.delete().eq("id", mutation.payload.id as string);
              break;
          }

          if (result?.error) {
            throw result.error;
          }

          await removeMutation(mutation.id);
          synced++;
        } catch {
          await incrementRetry(mutation.id);
          failed++;
        }
      }

      const count = await getPendingCount();
      setPendingCount(count);

      if (synced > 0) {
        toast.success(`Synced ${synced} offline change${synced > 1 ? "s" : ""}`);
      }
      if (failed > 0) {
        toast.warning(`${failed} change${failed > 1 ? "s" : ""} failed to sync`);
      }
    } catch (err) {
      console.error("Sync error:", err);
    } finally {
      setIsSyncing(false);
      syncingRef.current = false;
    }
  }, []);

  return { isOnline, isSyncing, pendingCount, syncPendingMutations };
}
